import apiClient from "../lib/api-client";

export interface Store {
  id: string;
  tenantId?: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  gstNumber?: string;
  licenseNumber?: string;
  licenseExpiry?: string | null;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  tenant?: {
    id: string;
    name: string;
  };
  _count?: {
    users?: number;
    invoices?: number;
  };
}

export interface StoresResponse {
  data: Store[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CreateStoreDto {
  tenantId: string;
  name: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  gstNumber?: string;
  licenseNumber?: string;
  licenseExpiry?: string | null;
  status?: string;
}

export interface UpdateStoreDto extends Partial<CreateStoreDto> {}

export interface PharmacyInformation {
  store: {
    id: string;
    name: string;
    address: string;
    city: string;
    state: string;
    pincode: string;
    phone: string | null;
    gstNumber: string | null;
    licenseNumber: string | null;
    licenseExpiry: string | null;
  };
  accountContact: {
    email: string;
    phone: string | null;
  };
  drugLicenseSource: "STORE" | "REGISTER" | null;
  detailedDrugLicense: {
    id: string;
    licenseNumber: string;
    licenseType: string;
    issueDate: string;
    expiryDate: string;
    status: string;
  } | null;
  fssaiRegistration: {
    id: string;
    fssaiNumber: string;
    registrationType: string;
    issueDate: string;
    expiryDate: string;
    status: string;
    productsAllowed: string | null;
  } | null;
}

export interface UpdatePharmacyInformationData {
  name: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
  gstNumber?: string | null;
  licenseNumber?: string | null;
  licenseExpiry?: string | null;
  fssai?: {
    fssaiNumber: string;
    registrationType: "BASIC" | "STATE" | "CENTRAL";
    issueDate: string;
    expiryDate: string;
    productsAllowed?: string | null;
  } | null;
}

export const storeService = {
  async getAll(): Promise<Store[]> {
    const response = await apiClient.get("/stores");
    // Handle both paginated response and direct array
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  },

  async getStores(params?: {
    page?: number;
    limit?: number;
  }): Promise<StoresResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append("page", params.page.toString());
    if (params?.limit) searchParams.append("limit", params.limit.toString());
    const response = await apiClient.get(`/stores?${searchParams}`);
    return response.data;
  },

  async getById(id: string): Promise<Store> {
    const response = await apiClient.get(`/stores/${id}`);
    return response.data;
  },

  async getPharmacyInformation(id: string): Promise<PharmacyInformation> {
    const response = await apiClient.get(`/stores/${id}/pharmacy-information`);
    return response.data;
  },

  async updatePharmacyInformation(
    id: string,
    data: UpdatePharmacyInformationData,
  ): Promise<PharmacyInformation> {
    const response = await apiClient.patch(
      `/stores/${id}/pharmacy-information`,
      data,
      {
        headers: {
          "Idempotency-Key": crypto.randomUUID(),
          "X-Change-Reason": "Update pharmacy information",
        },
      },
    );
    return response.data;
  },

  async create(data: CreateStoreDto): Promise<Store> {
    const response = await apiClient.post("/stores", data);
    return response.data;
  },

  async update(id: string, data: UpdateStoreDto): Promise<Store> {
    const response = await apiClient.patch(`/stores/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/stores/${id}`);
  },
};
